var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { animated, useSpring } from '@react-spring/web';
import styled from 'styled-components';
import CloseIcon from '../close-icon';
import Button from '../button';
import { SkeletonImage } from '../skeleton/image';
import useOnClickOutside from '../hooks/use-click-outside';
import { shadow } from '../theme/shadow';
const StyledAlert = styled(animated.div) `
  width: 260px;
  height: min-content;
  background-color: white;
  position: fixed;
  left: 0;
  right: 0;
  top: 0px;
  bottom: 0px;
  border-radius: 11px;
  margin: auto auto;
  z-index: 1000;
  box-shadow: ${shadow.lg};
  display: flex;
  flex-direction: column;

  div[data-role='alert-body'] {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
  }

  div.alert-icon-placeholder {
    width: 55px;
    height: 55px;
  }

  p[data-role='alert-body-title'] {
    font-weight: bold;
    margin: 12px 0px;
  }

  p[data-role='alert-body-content'] {
    font-weight: normal;
    font-size: 11px;
    color: #232526;
    margin: 0px 0px 12px 0px;
  }

  button[data-role='alert-button'] {
    width: 100%;
    margin-bottom: 8px;
  }

  button[data-role='alert-button']:last-child {
    margin-bottom: 0px;
  }
`;
const StyledMask = styled(animated.div) `
  position: fixed;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  background-color: rgba(0, 0, 0, 0.65);
`;
const StyledAlertBar = styled.div `
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px;
  border-bottom: ${() => '1px solid var(--border-color)'};
`;
export const Alert = React.forwardRef((_a, ref) => {
    var { closeable = true, maskCloseable = true, onOk, onCancel, onClose } = _a, props = __rest(_a, ["closeable", "maskCloseable", "onOk", "onCancel", "onClose"]);
    const modalRef = useRef();
    const maskRef = useRef();
    const [visible, setVisible] = useState(true);
    const controlled = typeof props.visible === 'boolean';
    const finalVisible = controlled ? props.visible : visible;
    const handleToggleModal = useCallback(() => {
        setVisible((prev) => !prev);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [onClose]);
    const handleCancel = useCallback(() => {
        setVisible(false);
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }, [onCancel]);
    const handleOk = useCallback(() => {
        setVisible(false);
        onOk === null || onOk === void 0 ? void 0 : onOk();
    }, [onOk]);
    const includes = useMemo(() => {
        if (maskCloseable) {
            return [];
        }
        return [maskRef];
    }, [maskRef, maskCloseable]);
    useOnClickOutside(modalRef, handleToggleModal, { disable: !finalVisible, includes });
    const modal = useSpring({
        scale: finalVisible ? 1 : 0,
        opacity: finalVisible ? 1 : 0,
    });
    return createPortal(React.createElement("div", { ref: ref },
        React.createElement(StyledAlert, { className: cx('alert', props.className), "data-role": "alert", ref: (dom) => (modalRef.current = dom), style: Object.assign(Object.assign({}, props.style), modal) },
            closeable ? (React.createElement(StyledAlertBar, { className: "alert-title", "data-role": "alert-title", style: props.style },
                React.createElement(CloseIcon, { position: "absolute", onClick: handleToggleModal }))) : null,
            React.createElement("div", { className: "alert-body", "data-role": "alert-body" },
                React.createElement(SkeletonImage, { size: "sm" }),
                React.createElement("p", { className: "alert-body-title", "data-role": "alert-body-title" }, props.title),
                React.createElement("p", { className: "alert-body-content", "data-role": "alert-body-content" }, props.content),
                React.createElement(Button, { buttonTheme: "primary", onClick: handleOk, className: "alert-button", "data-role": "alert-button" }, "OK"),
                React.createElement(Button, { buttonTheme: "primary", onClick: handleCancel, className: "alert-button", "data-role": "alert-button" }, "CANCEL"))),
        React.createElement(StyledMask, { ref: (dom) => (maskRef.current = dom), "data-role": "alert-mask", className: "alert-mask", style: {
                opacity: modal.opacity,
                zIndex: modal.opacity.to((x) => (x === 0 ? -1 : 999)),
            }, role: "button" })), document.body);
});
